import React, { useState } from 'react';
import styled from 'styled-components';
import { Accordion, AccordionDetails, AccordionSummary } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faAngleDown } from '@fortawesome/pro-light-svg-icons';
import { Contribution, ContributionInvestment } from './api';
import { contributionHeaders } from './ContributionHeaders';
import ContributionEmptyState from './ContributionEmptyState';
import TableWithLazyLoading from 'shared/components/Grid/TableWithLazyLoading';
import TrueImpactText from 'shared/design-system/base/TrueImpactText';
import { requestSuccess } from 'shared/auth/hooks/useRequestState';
import { COLORS } from 'shared/design-system/settings/variables/Colors';
import { SPACERS } from 'shared/design-system/settings/variables/Spacers';

interface ContributionYearSectionProps {
  yearPortfolios: Contribution[];
  onContributionClick: (
    c: Contribution,
    ci: ContributionInvestment,
    i: number,
  ) => unknown;
}

const StyledAccordion = styled(Accordion)`
  &.MuiAccordion-root {
    box-shadow: none;
    border: 1px solid ${COLORS.grayLight};
    border-radius: 4px !important;
    margin-bottom: ${SPACERS.small};
    &:before {
      display: none;
    }
    &.Mui-expanded {
      margin-bottom: ${SPACERS.small};
    }
  }
  .MuiAccordionSummary-root {
    background-color: ${COLORS.greyLightest};
    border-radius: 4px;
    min-height: 48px;
    padding: 0 ${SPACERS.medium};
    &.Mui-expanded {
      border-radius: 4px 4px 0 0;
    }
  }
  .MuiAccordionSummary-content {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin: ${SPACERS.small} 0;
  }
  .MuiAccordionDetails-root {
    padding: 0;
    overflow-x: auto;
  }
`;

const ContributionYearSection: React.FC<ContributionYearSectionProps> = ({
  yearPortfolios,
  onContributionClick,
}) => {
  const [expanded, setExpanded] = useState<number | false>(false);

  const handleChange = (id: number) => (_: React.SyntheticEvent, isExpanded: boolean) => {
    setExpanded(isExpanded ? id : false);
  };

  return (
    <>
      {yearPortfolios.map(yp => (
        <StyledAccordion
          key={yp.id}
          expanded={expanded === yp.id}
          onChange={handleChange(yp.id)}
          disableGutters
        >
          <AccordionSummary
            expandIcon={<FontAwesomeIcon icon={faAngleDown} />}
          >
            <TrueImpactText variant="body-bold">
              {yp.year || yp.name}
            </TrueImpactText>
            <TrueImpactText variant="body-small">
              {yp.investments.length} investment{yp.investments.length !== 1 ? 's' : ''}
            </TrueImpactText>
          </AccordionSummary>
          <AccordionDetails>
            {yp.investments.length === 0 ? (
              <ContributionEmptyState />
            ) : (
              <TableWithLazyLoading
                headers={contributionHeaders(yp, (d, r, i) =>
                  onContributionClick(d, r, i),
                )}
                resultCount={yp.investments.length}
                filters={{}}
                requestState={requestSuccess(yp.investments)}
                loadMore={() => null}
                allowLoadMore={false}
                RenderEmptyState={ContributionEmptyState}
              />
            )}
          </AccordionDetails>
        </StyledAccordion>
      ))}
    </>
  );
};

export default ContributionYearSection;
